import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, capturePageview } from "@/lib/analytics";
import { hasConsent, onConsentChange } from "@/lib/consent";
import { initBrevoConversations } from "@/lib/chat";
import CookieConsent from "@/components/CookieConsent";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import Services from "@/pages/Services";
import BookingFlow from "@/pages/BookingFlow";
import Inquiry from "@/pages/Inquiry";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import PaymentReturn from "@/pages/PaymentReturn";
import About from "@/pages/About";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Security from "@/pages/Security";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Book from "@/pages/Book";
import BookingConfirmed from "@/pages/BookingConfirmed";
import Login from "@/pages/Login";
import Proof from "@/pages/Proof";
import ProofRxp from "@/pages/proof/ProofRxp";
import ProofAiops from "@/pages/proof/ProofAiops";
import ProofStudioRetainer from "@/pages/proof/ProofStudioRetainer";
import ProofRevenuePerformance from "@/pages/proof/ProofRevenuePerformance";
import Quote from "@/pages/Quote";
import Contact from "@/pages/Contact";
import Parse from "@/pages/Parse";
import Playbook from "@/pages/Playbook";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Agent from "@/pages/Agent";
import AgentAgreement from "@/pages/AgentAgreement";
import AdminAgentAgreements from "@/pages/admin/AgentAgreements";
import InsightsComposer from "@/pages/admin/InsightsComposer";
import InsightDraftEditor from "@/pages/admin/InsightDraftEditor";

function AppRouter() {
  const location = useLocation();

  // PostHog pageview on every route change (we disabled the SDK's built-in
  // pageview capture in favour of explicit, SPA-aware tracking).
  useEffect(() => {
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Synchronous check before normal routing: handle OAuth fragment first.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:sku" element={<Services />} />
          <Route path="/proof" element={<Proof />} />
          <Route path="/proof/rxp" element={<ProofRxp />} />
          <Route path="/proof/aiops" element={<ProofAiops />} />
          <Route path="/proof/studio-retainer" element={<ProofStudioRetainer />} />
          <Route path="/proof/revenue-performance" element={<ProofRevenuePerformance />} />
          <Route path="/book/:serviceId" element={<BookingFlow />} />
          <Route path="/inquiry" element={<Inquiry />} />
          <Route path="/about" element={<About />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          <Route path="/security" element={<Security />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<Navigate to="/terms#refunds" replace />} />
          <Route path="/book" element={<Book />} />
          <Route path="/booking-confirmed" element={<BookingConfirmed />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<ClientDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/agent-agreements" element={<AdminAgentAgreements />} />
          <Route path="/admin/insights" element={<InsightsComposer />} />
          <Route path="/admin/insights/:id" element={<InsightDraftEditor />} />
          <Route path="/payment-return" element={<PaymentReturn />} />
          <Route path="/quote/:id" element={<Quote />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/parse" element={<Parse />} />
          <Route path="/playbooks/:slug" element={<Playbook />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/agent" element={<Agent />} />
          <Route path="/agent-agreement/:id" element={<AgentAgreement />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    // Gate non-essential trackers behind cookie consent. Initialize immediately
    // if the visitor has already opted in on a prior visit; otherwise subscribe
    // to consent changes so we boot them up the moment they Accept.
    const bootGated = () => {
      if (hasConsent("analytics")) initAnalytics();
      if (hasConsent("chat")) initBrevoConversations();
    };
    bootGated();
    const off = onConsentChange(bootGated);
    return off;
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieConsent />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
